import Request from "./request";
import ConsentConfig from "../../config/consent-config";

const CONSENTSTORAGEKEY = "userConsentData";
// 30 mins
const CONSENTDATAEXP = 30 * 60 * 1000;

class Consent {
    constructor() {
        this.request = new Request(ConsentConfig);
        this.endpoint = "consent"
    }

    async getClientRegion() {
        const MAX_WAIT = 1500; // 1.5s timeout for adblockers / network blocks
        const REGION_DEFAULT = { region: "PH", type: "" };
        return new Promise((resolve) => {
            let resolved = false;

            const safeResolve = (value) => {
                if (!resolved) {
                    resolved = true;
                    resolve(value);
                }
            };
            // Testing
            //safeResolve({ region: "GB", type: "TCF" });
            //safeResolve({ region: "US-CA", type: "CPRA" });
            //safeResolve({ region: "US-VA", type: "VCDPA" });
            //safeResolve({ region: "PH", type: "" });
            //return;

            // Timeout fallback — guarantees the promise ALWAYS resolves
            const timeout = setTimeout(() => {
                safeResolve(REGION_DEFAULT);
            }, MAX_WAIT);

            try {
                // Docs: https://country.is/
                /*
                 * No quotas. Infrastructure rate-limits to 10 requests per second per IP to prevent abuse.
                 */

                const ipRequest = new Request({ protocol: "https", host: "api.country.is" });
                ipRequest.send(
                    "?fields=subdivision",
                    "GET"
                ).then((data) => {
                        clearTimeout(timeout);

                        // Validate response shape
                        if (!data || typeof data !== "object") {
                            return safeResolve(REGION_DEFAULT);
                        }

                        if (data.error) {
                            return safeResolve(REGION_DEFAULT);
                        }

                        const countryCode = data.country || "";
                        const regionCode = data.subdivision || "";

                        let cmpType = "";

                        // United States (CPRA / VCDPA)
                        if (countryCode === "US") {
                            if (regionCode === "CA") {
                                cmpType = "CPRA";
                            } else if (["VA", "CO", "CT", "UT"].includes(regionCode)) {
                                cmpType = "VCDPA";
                            }

                            return safeResolve({ region: `US-${regionCode}`, type: cmpType });
                        }

                        // EU / UK (TCF)
                        if (["GB", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
                            "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE"
                        ].includes(countryCode)) {
                            return safeResolve({ region: countryCode, type: "TCF" });
                        }

                        // Rest of world
                        return safeResolve({ region: countryCode, type: "" })
                    })
                    .catch(() => {
                        clearTimeout(timeout);
                        safeResolve(REGION_DEFAULT);
                    });

            } catch (err) {
                clearTimeout(timeout);
                safeResolve(REGION_DEFAULT);
            }
        });
    }

    async fetchConsent(userId, type = "cookie", useApi = true) {
        /*
         * userId = cookie user id
         * type = type of consent
         *      - 'cookie' for general cookies
         *      - '...' for checkout
         *      - '...' for franchise/course registration
         * useApi = whether to trigger an API call
         */

        if (type === undefined) {
            throw new Error("Parameter type is required")
        }

        // Make it URL safe
        const encodedUserId = encodeURIComponent(userId);

        // Store user's consent data to localstorage to avoid multiple API calls

        /*
         * If user consent data is already stored in the localstorage, check expiration.
         * If not expired, return the cached list.
         * Otherwise, make an API call to fetch consent data, then store to localstorage
         */
        const promise = new Promise(async (resolve, reject) => {
            let userConsentData = localStorage.getItem(CONSENTSTORAGEKEY);

            let useCache = false;

            if (userConsentData !== null) {
                try {
                    userConsentData = JSON.parse(userConsentData);

                    if (Date.now() < userConsentData.exp) {
                        useCache = true;
                    }
                } catch (error) { }
            }

            if (useCache === true) {
                // If there is a non-expired cache
                resolve(userConsentData);
            } else {
                // If cache is expired or no cache
                try {
                    let data = userConsentData ?? {};

                    if (useApi) {
                        data = await this.request.send(
                            `${this.endpoint}/${type}/main?userId=${encodedUserId}`,
                            "GET"
                        )
                    }

                    if (data.hasOwnProperty("consent")) {
                        // For future: handle different consent types, different keys
                        const exp = Date.now() + CONSENTDATAEXP;
                        localStorage.setItem(CONSENTSTORAGEKEY, JSON.stringify({ ...data, exp }));
                    }

                    resolve(data);
                } catch (err) {
                    reject(err);
                }
            }
        })

        return promise;
    }

    async writeConsent(userId, consent, method = "PUT", type = "cookie", useApi = true) {
        /*
         * userId = cookie user id
         * consent = consent object
         * method = "POST" (new consent), "PUT" (existing consent)
         * type = type of consent
         *      - 'cookie' for general cookies
         *      - '...' for checkout
         *      - '...' for franchise/course registration
         * useApi = whether to trigger an API call
         */

        if (type === undefined) {
            throw new Error("Parameter type is required")
        }

        const body = {
            userId,
            consent
        }

        const promise = new Promise(async (resolve, reject) => {
            try {
                let data = {};

                if (useApi) {
                    data = await this.request.send(
                        `${this.endpoint}/${type}/main`,
                        method,
                        body
                    )
                } else  {
                    data = { ...body, updatedAt: new Date().toISOString() }
                }

                if (data.hasOwnProperty('consent')) {
                    const exp = Date.now() + CONSENTDATAEXP;
                    localStorage.setItem(CONSENTSTORAGEKEY, JSON.stringify({ ...data, exp }));
                }

                resolve(data);
            } catch (err) {
                reject(err)
            }
        })

        return promise;
    }
}

export default Consent;